import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Tooltip from 'sushiweb/Tooltip';
import Download from 'sushiweb/Icons/all/Download';
import {
  exportCSV,
  formatAmountInRupees,
  formatDateinDDMMMYYY,
  formatReferenceIDForPayments,
  getCurrentDate,
  searchWithName,
} from '../../../helpers';
import DataTables from '../../common/components/DataTables/DataTables';
import {
  paymentStatusEnum,
  REFRESH_LABEL,
  sourceToCreditType,
  statusLabels,
} from '../../common/components/constants';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import { paymentsCreditsHeadColumn } from '../../common/components/DataTables/components/headColumns';

const GiftingProgramPaymentTable = ({
  selectedWorkspace,
  programDetails,
  programPayments,
  fetchingProgramPayments,
  fetchedProgramPayments,
  getPaymentsForProgram,
}) => {
  const keyFields = ['reference_id', 'created_on'];

  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const { programId } = useParams();
  const [search, onInputChange] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const { payments = [] } = programPayments;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPaymentsForProgram({
      workspaceId: selectedWorkspace?.workspace_id,
      programId,
    });
  }, []);

  const refreshPage = () => {
    getPaymentsForProgram({
      workspaceId: selectedWorkspace?.workspace_id,
      programId,
    });
  };

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingProgramPayments && fetchedProgramPayments) {
      if (payments) {
        payments.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (payments.length === 0) {
          setDisabledSearch(true);
        }
        setTableData(payments);
        setCurrentData(payments.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingProgramPayments, fetchedProgramPayments]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingProgramPayments) {
        const arr = searchWithName(payments, search, keyFields);
        setSearchResults(arr);
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, fetchingProgramPayments]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };
  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'created_on_val', value: 'Date' },
      { key: 'source_val', value: 'Payment type' },
      { key: 'reference_id_val', value: 'UTR/Reference no.' },
      { key: 'amount_val', value: 'Amount' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.created_on_val = formatDateinDDMMMYYY(data.created_on);
      labelledData.source_val = sourceToCreditType(data.source);
      labelledData.reference_id_val = formatReferenceIDForPayments(data);
      labelledData.amount_val = formatAmountInRupees(String(data.amount));
      labelledData.status = statusLabels[paymentStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `${programDetails.name} - Program Payment - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    padding: '0px 20px 10px 0px',
    cursor: 'pointer',
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by reference no.'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by reference no.
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilterAndExportCSVContainer>
          {disabledSearch ? (
            <Download color='grey' style={downloadCSVStyle} />
          ) : (
            <Tooltip
              position='top'
              label='Download the gc payments'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
                padding: '10px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedProgramPayments ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            columns={paymentsCreditsHeadColumn}
            loading={fetchingProgramPayments || loading}
            data={currentData}
            pagination
            totalRows={searchResults.length}
            handlePageChange={handlePageChange}
            onSort={handleSort}
            emptyStateTitle='No payments for program'
          />
        )}
      </TableContainer>
    </>
  );
};

GiftingProgramPaymentTable.propTypes = {
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  programDetails: PropTypes.objectOf(PropTypes.any),
  programPayments: PropTypes.objectOf(PropTypes.any),
  getPaymentsForProgram: PropTypes.func,
  fetchingProgramPayments: PropTypes.bool,
  fetchedProgramPayments: PropTypes.bool,
};

GiftingProgramPaymentTable.defaultProps = {
  selectedWorkspace: {},
  programDetails: {},
  programPayments: {},
  getPaymentsForProgram: _noop,
  fetchingProgramPayments: true,
  fetchedProgramPayments: true,
};

export default GiftingProgramPaymentTable;
